import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "./config";
import PageLoader from "./PageLoader";
import applyCommunityFont, { applyCommunityDirection, applyCommunityTheme } from "./applyFont";
import { useBrand } from "./useBrand";
import { ReferralLink } from "./MembershipViews";
import { instructorsOf, InstructorChips } from "./instructors";
import { BrandLogo } from "./MembershipHeader";
import { groupLevelLabel, isCourseLinkedGroup } from "./groupLevelGate";

// Normalize a video URL / embed snippet into an embeddable iframe src.
function embedUrl(raw) {
  let url = (raw || "").trim();
  const iframe = url.match(/src="([^"]+)"/);
  if (iframe) url = iframe[1];
  if (url.includes("youtube.com/watch")) {
    try {
      const v = new URL(url).searchParams.get("v");
      if (v) url = `https://www.youtube.com/embed/${v}`;
    } catch {
      /* ignore */
    }
  } else if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1]?.split(/[?&]/)[0];
    if (id) url = `https://www.youtube.com/embed/${id}`;
  } else if (url.includes("vimeo.com/") && !url.includes("player.vimeo")) {
    const id = url.split("vimeo.com/")[1]?.split(/[?&]/)[0];
    if (id) url = `https://player.vimeo.com/video/${id}`;
  }
  return url;
}

// Default marketing content shown when a group hasn't set up its landing page
// (mirrors guruai's $defaultSkills fallback in landing_page_preview.blade.php).
const DEFAULT_SKILL_HEADING = "When You Join This Community, You'll Get Access To:";
const DEFAULT_SKILLS = [
  "Exclusive trainings and insider knowledge",
  "High-value resources and downloadable guides",
  "Proven frameworks and strategies for real results",
  "Step-by-step learning content you can apply immediately",
  "Community discussions and support",
  "Access to special workshops, masterclasses, and events",
  "Tools and resources to accelerate your progress",
  "Expert insights and real-world strategies",
  "Continuous updates and new learning opportunities",
];

/**
 * Public group landing page — /:token?gid={group_token}
 * Shows the group's description, media gallery and skills (marketing page),
 * mirroring guruai's landing page preview.
 */
export default function GroupLanding({ token, gid, member, onBack, onJoin, onLogin, onInstructor, embedded = false, referral = null }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);
  // Join request in flight — keeps the CTA from firing twice before the
  // server has answered.
  const [joining, setJoining] = useState(false);
  // Guards every state write: the fetch below outlives the component when a
  // member navigates away mid-request.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Extracted so the join CTA can re-read the group after sending a request:
  // `pending` is owned by the server (a status-0 membership_group_members row),
  // and re-reading it is what keeps "Request Sent" honest — an optimistic flag
  // would stick even when the request actually failed.
  const fetchLanding = useCallback(
    ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError("");
      }

      return axios
        .post(`${config.COMMUNITY_API_URL}/membership-group-landing`, {
          community_token: token,
          group_token: gid,
          community_member_id: member?.community_member_id || undefined,
          // Needed for the backend to verify membership (joined => referral link
          // instead of the Join button on the in-feed About tab).
          member_token: member?.auth_token,
        })
        .then((res) => {
          if (!alive.current) return;
          if (res.data?.error === 0) {
            setData(res.data);
            applyCommunityFont(res.data.community?.membership_font);
            applyCommunityDirection(res.data.community?.rtl);
            applyCommunityTheme(res.data.community?.brand);
            // A silent refresh must not throw the reader back to the first tab.
            if (!silent) setActive(0);
          } else if (!silent) {
            setError(res.data?.msg || "Landing page not found.");
          }
        })
        .catch(() => {
          if (alive.current && !silent) setError("Something went wrong.");
        })
        .finally(() => {
          if (alive.current && !silent) setLoading(false);
        });
    },
    [token, gid, member]
  );

  useEffect(() => {
    fetchLanding();
  }, [fetchLanding]);

  // Brand from the login page's source (getfunnel). Called before the early
  // returns below so hook order stays stable.
  const { primary } = useBrand(token);

  if (loading) {
    return (
      <PageLoader embedded={embedded} page="Community" primary={primary} />
    );
  }
  if (error || !data) {
    return (
      <div className={`${embedded ? "py-20" : "min-h-screen"} flex flex-col items-center justify-center gap-3 text-gray-500`}>
        <p>{error || "Not found"}</p>
        <button onClick={onBack} className="text-sm font-medium text-gray-700 underline">
          Back to community
        </button>
      </div>
    );
  }

  const { community, group, landing } = data;
  const media = landing.media || [];
  const cur = media[active];
  // Access type: 1 = Public, 2 = Private. Pricing is separate.
  const accessType = Number(group.access_type);
  const isPrivate = accessType === 2;
  const pricing = group.pricing_type || "free";
  const isPaid = pricing === "paid";
  const isTrial = pricing === "free_trial";
  const accessLabel = isPrivate ? "Private" : "Public";
  const priceLabel = group.price_label || "";
  // Bare amount only (strip the "Free trial · " prefix) for the left meta.
  const amountLabel = priceLabel.replace(/^Free trial · /, "");

  // Default image: backend already resolves group.banner to the default banner
  // when the group has none, so use it both for an empty gallery and as the
  // onError fallback for any broken media URL.
  const defaultImage = group.banner;

  // Default content: when the group hasn't added its own skills, fall back to
  // the default checklist (mirrors guruai's landing page preview).
  const hasSkills = landing.skill_list?.length > 0;
  const skillHeading = hasSkills ? landing.skill_heading : DEFAULT_SKILL_HEADING;
  const skillList = hasSkills ? landing.skill_list : DEFAULT_SKILLS;

  const description = (group.description || "").trim();
  const shortDescription =
    description.length > 500 ? `${description.slice(0, 500)}...` : description;
  // Free-trial members whose trial has lapsed must subscribe to keep access.
  const trialLapsed = isTrial && group.joined && group.trial_expired;
  // A private group's join is a REQUEST an admin must approve. Until then the
  // member has a status-0 row and the server reports `pending` — so the CTA has
  // to say so and stop accepting clicks, instead of inviting them to request
  // again (which just re-sends the same pending request).
  const requestPending = !group.joined && !!group.pending;

  // Send the request, then re-read the group so the button flips only on a
  // result the server actually recorded. `onJoin` returns a promise in some
  // hosts and nothing in others; awaiting handles both.
  const handleJoin = async () => {
    if (requestPending || joining) return;
    setJoining(true);
    let result;
    try {
      result = await onJoin(group);
    } finally {
      if (alive.current) setJoining(false);
    }
    if (!alive.current) return;

    // Preferred path: the host returns the join response, which already carries
    // `pending`. Flip from it directly so the CTA changes the instant the
    // request is recorded — the same moment the "Request sent" toast appears.
    if (result?.error === 0 && result.pending) {
      setData((prev) =>
        prev ? { ...prev, group: { ...prev.group, pending: true } } : prev
      );
      return;
    }

    // Fallback for hosts whose onJoin resolves to nothing (the in-feed About
    // tab): ask the server what the state is now.
    fetchLanding({ silent: true });
  };

  // Disabled while the request is outstanding AND while one is in flight, so a
  // double-click can't send two.
  const ctaDisabled = requestPending || joining;

  /*
   * UNLOCK AT A LEVEL — earned, not bought.
   *
   * A level group has no product and no join the bridge would accept: the door
   * is the member's points against the level's threshold. So the CTA states the
   * level instead of offering a purchase or a join, the same way a level-gated
   * COURSE states it (CourseViews' lockMsg). The level's NAME leads, because its
   * number is only the creator's list position and cannot be read as a rank.
   */
  const isLevelGated = (group.pricing_type || "") === "level" || Number(group.unlock_level) > 0;
  const levelCta = groupLevelLabel(group);

  const joinLabel = requestPending
    ? "Request Sent"
    : group.instructor_access
    ? // Instructors have access to every group, paid ones included — so the CTA
      // is entry, never purchase. Checked first because the paid branch below
      // ignores `joined` and would otherwise offer to sell them their own group.
      "Enter community"
    : isLevelGated && group.locked
    ? levelCta
    : isPaid
    ? // Paid groups require payment — always show "Get access" (no free entry).
      priceLabel
      ? `Get access — ${priceLabel}`
      : "Get access"
    : group.locked
    ? // A FREE group can be gated too: putting one behind a product is how a
      // creator hands it out, and the bridge then refuses a direct join and
      // routes the click to that product's page. The label has to say so —
      // "Join Group" on a button that opens a checkout is a promise it does not
      // keep. Price and entry are separate axes, so this is deliberately keyed
      // on `locked` rather than on the group being paid.
      "Get access"
    : trialLapsed
    ? priceLabel
      ? `Buy now — ${priceLabel}`
      : "Buy now"
    : group.joined
    ? "Enter community"
    : isTrial
    ? group.trial_duration
      ? `Start ${group.trial_duration}-day free trial`
      : "Start free trial"
    : isPrivate
    ? "Request to join"
    : "Join Community";
  // "Sending…" only while in flight; `joinLabel` already reads "Request Sent"
  // once the server has recorded it.
  const ctaLabel = joining ? "Sending…" : joinLabel;

  const postCount = group.post_count ?? 0;
  // The third sidebar stat is labelled "Courses" but populated from the
  // group's comment count (per design decision).
  const courseStat = group.comment_count ?? 0;

  // Access type + member count + pricing + instructor — premium pill chips.
  const metaRow = (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Access type — Public (globe) or Private (lock), theme-tinted */}
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold"
        style={{ backgroundColor: `${primary}14`, color: primary }}
      >
        {isPrivate ? (
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm6.93 6h-2.95a15.7 15.7 0 00-1.38-3.56A8.03 8.03 0 0118.93 8zM12 4c.83 1.2 1.48 2.53 1.91 4h-3.82c.43-1.47 1.08-2.8 1.91-4zM4.26 14a7.96 7.96 0 010-4h3.38a16.5 16.5 0 000 4H4.26zm.81 2h2.95c.35 1.27.82 2.47 1.38 3.56A8.03 8.03 0 015.07 16zm2.95-8H5.07a8.03 8.03 0 014.33-3.56A15.7 15.7 0 008.02 8zM12 20c-.83-1.2-1.48-2.53-1.91-4h3.82A13.4 13.4 0 0112 20zm2.34-6H9.66a14.7 14.7 0 010-4h4.68a14.7 14.7 0 010 4zm.27 5.56c.56-1.09 1.03-2.29 1.38-3.56h2.95a8.03 8.03 0 01-4.33 3.56zM16.36 14a16.5 16.5 0 000-4h3.38a7.96 7.96 0 010 4h-3.38z" />
          </svg>
        )}
        {accessLabel}
      </span>
      {/* Member count */}
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-[13px] font-semibold text-gray-600">
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" />
        </svg>
        {group.member_count} members
      </span>
      {/* Pricing type — Free (green) / Paid · trial (red), matching the cards */}
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ${
          pricing === "free" ? "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300" : "bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300"
        }`}
      >
        {pricing === "free" ? (
          /* gift = Free */
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M3 8h18v4H3V8zm9 0S10 2 7 4s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z" />
          </svg>
        ) : pricing === "free_trial" ? (
          /* clock = Free trial */
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          /* dollar = Paid */
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M16.5 7.5A4 4 0 0012.5 5h-1a3.5 3.5 0 000 7h1a3.5 3.5 0 010 7h-1a4 4 0 01-4-2.5" />
          </svg>
        )}
        {/* EARNED first: a level group costs nothing but is not open, and
            "Free" on it reads as "come in". Same order CourseSearch uses for a
            level-gated course, and the level's NAME leads over its number. */}
        {isCourseLinkedGroup(group) || pricing === "level"
          ? groupLevelLabel(group)
          : pricing === "free"
          ? "Free"
          : pricing === "free_trial"
          ? `Free trial${amountLabel ? ` · ${amountLabel}` : ""}`
          : `Paid${amountLabel ? ` · ${amountLabel}` : ""}`}
      </span>
      {/* Instructors — clickable "By <name>" chip(s) opening the instructor page.
          Several render as an avatar stack; one is unchanged. */}
      <InstructorChips
        list={instructorsOf(group)}
        onInstructor={onInstructor}
        primary={primary}
        className={`inline-flex items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-1 text-[13px] font-semibold text-gray-600 transition hover:bg-gray-200 hover:text-gray-900 ${
          instructorsOf(group).length > 1 ? "pr-1.5" : "pr-3"
        }`}
      />
    </div>
  );

  return (
    <div className={embedded ? "bg-surface" : "min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100"}>
      {/* header — logo + Log In (guests) / Enter group (members). Hidden when
          embedded (e.g. the group "About" tab already sits under a header). */}
      {!embedded && (
      <header className="bg-surface border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-[1000px] mx-auto px-5 h-14 flex items-center justify-between">
          <BrandLogo community={community} className="h-8 w-auto max-w-[140px]" />
          {member ? (
            <button
              onClick={handleJoin}
              disabled={ctaDisabled}
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              className={`text-sm font-semibold text-white px-4 py-2 rounded-lg transition ${
                ctaDisabled ? "cursor-not-allowed opacity-60" : "hover:opacity-90"
              }`}
            >
              {ctaLabel}
            </button>
          ) : isPaid ? (
            <button
              onClick={() => onJoin(group)}
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              className="text-sm font-semibold text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
            >
              Get Access
            </button>
          ) : (
            <button
              onClick={() => onLogin(group)}
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              className="text-sm font-semibold text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
            >
              Log In
            </button>
          )}
        </div>
      </header>
      )}

      {/* page wrap */}
      <div className="max-w-[1000px] mx-auto px-5 pb-16 pt-6">
        {/* Back to the group list. Standalone view only — when embedded this
            sits inside a tab that already has its own navigation, and onBack
            there means "return to the feed", not "leave the group". */}
        {!embedded && onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to communities"
            className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-surface px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition hover:border-gray-300 hover:text-gray-900"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 15.5 6.5 10 12 4.5" />
            </svg>
            Back
          </button>
        ) : null}

        {/* hero card — banner/media + title/meta grouped in one elevated box */}
        <div className="bg-surface rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-4 sm:p-6 max-w-[1030px]">
        {/* banner / media section — directly below the header */}
        <div>
          <div className="flex flex-col lg:flex-row gap-3">
            {/* main preview */}
            <div className="flex-1 min-w-0">
              <div className="h-[300px] max-md:h-[150px] bg-[#f8f9fa] dark:bg-canvas rounded-[10px] overflow-hidden shadow-sm flex items-center justify-center">
                {cur?.type === "video" ? (
                  <iframe
                    src={embedUrl(cur.url)}
                    title={group.name}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={(cur?.image) || defaultImage}
                    alt={group.name}
                    className="w-full h-full object-fill"
                    onError={(e) => {
                      if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage;
                    }}
                  />
                )}
              </div>
            </div>

            {/* thumbnails */}
            {media.length > 0 && (
              <div className="lg:w-auto">
                <div className="flex lg:flex-col flex-row gap-2 mt-3 lg:mt-0 overflow-x-auto pb-1">
                  {media.map((m, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className="relative w-[50px] h-[50px] shrink-0 rounded-lg overflow-hidden border border-gray-300"
                      style={i === active ? { border: `3px solid ${primary}` } : undefined}
                    >
                      <img
                        src={(m.type === "video" ? m.thumb : m.image) || defaultImage}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage;
                        }}
                      />
                      {m.type === "video" && (
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[34px] h-[34px] rounded-full bg-black/60 flex items-center justify-center text-white">
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* group title + meta row (below the banner) */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-800 leading-tight max-md:text-[1.4rem]">
            {group.name}
          </h1>
          <div className="mt-3">{metaRow}</div>
        </div>
        </div>
        {/* /hero card */}

        {/* body: skills card + sidebar */}
        <div className="mt-8 max-w-[1030px]">
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* skills */}
            <div className="w-full lg:w-2/3">
              <div className="bg-surface rounded-2xl shadow-sm ring-1 ring-gray-200/70">
                <div className="p-8 max-md:p-6">
                  {skillHeading && (
                    <div className="mb-5 flex items-center gap-2.5">
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${primary}14`, color: primary }}
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <h3 className="text-xl font-bold tracking-tight text-gray-900">{skillHeading}</h3>
                    </div>
                  )}
                  {skillList?.length > 0 && (
                    <ul className="space-y-1">
                      {skillList.map((s, i) => (
                        <li
                          key={i}
                          className="group flex items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-gray-50"
                        >
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 transition-transform group-hover:scale-105"
                            style={{ backgroundColor: `${primary}14`, color: primary, "--tw-ring-color": `${primary}33` }}
                          >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span className="text-[15px] font-medium text-gray-700">{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* sidebar */}
            <div className="w-full lg:w-1/3 lg:sticky lg:top-20">
              <div className="bg-surface ring-1 ring-gray-200/70 rounded-2xl overflow-hidden shadow-sm">
                <div className="relative h-[140px] overflow-hidden">
                  <img
                    src={group.banner || defaultImage}
                    alt={group.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage;
                    }}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                </div>
                <div className="text-center px-4 py-4">
                  <h6 className="text-gray-800 font-bold text-lg leading-snug mb-1 text-left">
                    {group.name}
                  </h6>
                  {shortDescription && (
                    <p className="text-gray-500 text-sm text-left">{shortDescription}</p>
                  )}

                  <div className="mt-4 mb-4 flex items-center rounded-xl border border-gray-100 bg-gray-50/70 py-3">
                    <div className="flex-1 border-r border-gray-200/70">
                      <div className="text-lg font-extrabold leading-none" style={{ color: primary }}>{postCount}</div>
                      <small className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Posts</small>
                    </div>
                    <div className="flex-1 border-r border-gray-200/70">
                      <div className="text-lg font-extrabold leading-none" style={{ color: primary }}>{group.member_count ?? 0}</div>
                      <small className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Members</small>
                    </div>
                    <div className="flex-1">
                      <div className="text-lg font-extrabold leading-none" style={{ color: primary }}>{courseStat}</div>
                      <small className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Courses</small>
                    </div>
                  </div>

                  {/* When `referral` data is supplied (the in-feed "About" tab):
                      joined members get their referral link, everyone else gets
                      the Join button. The standalone landing page passes no
                      `referral`, so it always shows the button (unchanged). */}
                  {referral && group.joined ? (
                    <div className="text-left">
                      <ReferralLink
                        token={token}
                        groupToken={gid}
                        urlToken={referral.urlToken}
                        primary={primary}
                        points={referral.points}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={handleJoin}
                      disabled={ctaDisabled}
                      style={{ backgroundColor: primary, boxShadow: `0 8px 20px -8px ${primary}99` }}
                      className={`w-full text-white font-bold py-3 rounded-xl flex items-center justify-center gap-1.5 transition ${
                        ctaDisabled
                          ? "cursor-not-allowed opacity-60"
                          : "hover:-translate-y-0.5 hover:opacity-95"
                      }`}
                    >
                      {requestPending ? (
                        /* tick — the request is in, nothing left to click */
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.5l2.5 2.5 4.5-5" />
                        </svg>
                      ) : (
                        !group.joined && (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="9" />
                            <path strokeLinecap="round" d="M12 8v8M8 12h8" />
                          </svg>
                        )
                      )}
                      {ctaLabel}
                    </button>
                  )}

                </div>
              </div>
            </div>
          </div>

          {/* full landing description */}
          {landing.description?.trim() && (
            <div className="bg-surface rounded-2xl shadow-sm ring-1 ring-gray-200/70 mt-5">
              <div className="p-8 max-md:p-6">
                <div className="mb-4 flex items-center gap-2.5">
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${primary}14`, color: primary }}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.4-9.4a2 2 0 112.8 2.8L12 16l-4 1 1-4 9.6-9.6z" />
                    </svg>
                  </span>
                  <h3 className="text-xl font-bold tracking-tight text-gray-900">About this community</h3>
                </div>
                <div className="whitespace-pre-line leading-relaxed text-[15px] text-gray-600">
                  {landing.description}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
